import { CreationToolFrame, CreationToolSourceFile } from 'creation/src/creation-tools/types'
import CreationTool from 'creation/src/creation-tools/types/creation-tool'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import CreationToolStep from 'creation/src/creation-tools/types/steps'
import CreationToolUserRestriction from 'creation/src/creation-tools/types/user-restriction'
import getCreationSteps from 'creation/src/creation-tools/utils/get-creation-steps'
import { createStore, type StoreApi } from 'zustand/vanilla'
import { disposeFrames, disposeSources, disposeUnreachableCanvases } from './dispose'

export type CreationLoginContinuation = () => void | Promise<void>

export type CreationStoreInit = {
    tool: CreationTool
    basePath: string
    isLoggedIn: boolean
    userRestrictionAccess: CreationToolUserRestriction[]
    navigate: (url: string) => void
    onRequireLogin: (onSuccess?: CreationLoginContinuation) => void
}

export type CreationState = {
    tool: CreationTool
    // Session state, not flow state, same as isLoggedIn below: intentionally
    // left out of initialFlowState so reset() does not revert a mid-flow
    // permission change (e.g. verifying an account) back to what the store
    // was constructed with.
    userRestrictionAccess: CreationToolUserRestriction[]
    sources: CreationToolSourceFile[]
    outputType: CreationToolOutputType
    steps: CreationToolStep[]
    stepFrames: Partial<Record<CreationToolStep, CreationToolFrame[]>>
    currentAudioId: string
    isDraggingFile: boolean
    // Session state, not flow state: intentionally left out of
    // initialFlowState so reset() does not sign the user out of the store.
    isLoggedIn: boolean
    // Sources chosen while logged out, held so signing in can resume the flow
    // instead of making the user pick their file again. See setFiles.
    pendingFiles: { sources: CreationToolSourceFile[]; outputType: CreationToolOutputType } | null
    // Not legacy state — legacy's equivalent moment (manager.tsx:365-369) is
    // a full `location.href` page load, which tears down all JS state by
    // itself. This port uses a client-side router.push instead (upload-
    // store.ts's success sequence), which does NOT tear the store down, so
    // finalize/upload-step.tsx's `reset()` call (releasing canvases before
    // the push, per upload-store.spec.ts §6.3) makes `sources` empty while
    // the still-mounted RequireSources is watching it. Without this flag,
    // that transient emptiness reads exactly like "arrived at this step
    // with no sources" and RequireSources fires its own `router.replace`
    // to basePath — which, since it runs from a passive effect after the
    // reset-triggered re-render, wins the race against the earlier
    // `router.push` to the new GIF's page (CRITICAL 2 in the final
    // whole-branch review). markNavigatingAway is called before `reset` at
    // that one call site specifically so RequireSources never observes
    // "no sources" without also observing "leaving on purpose" in the same
    // render.
    isNavigatingAway: boolean
    markNavigatingAway: () => void
    setFiles: (sources: CreationToolSourceFile[], outputType: CreationToolOutputType) => void
    // Replays the sources stashed by a login-gated setFiles. No-op if none.
    resumePendingFiles: () => void
    setSources: (sources: CreationToolSourceFile[]) => void
    replaceSources: (sources: CreationToolSourceFile[]) => void
    setOutputType: (outputType: CreationToolOutputType) => void
    setCurrentAudioId: (id: string) => void
    setIsDraggingFile: (isDraggingFile: boolean) => void
    setIsLoggedIn: (isLoggedIn: boolean) => void
    setUserRestrictionAccess: (userRestrictionAccess: CreationToolUserRestriction[]) => void
    saveFrames: (frames: CreationToolFrame[], step: CreationToolStep) => void
    // Exposes init.onRequireLogin as a callable store action for flows that
    // must gate *before* doing any work (e.g. the url input firing analytics
    // and hitting validateSourceByInput) — see url-input.tsx. setFiles below
    // keeps its own, separate gate as the backstop for callers that don't
    // check first (e.g. file-button.tsx, which has no loggedIn check of its
    // own).
    requireLogin: (onSuccess?: CreationLoginContinuation) => void
    reset: () => void
}

const initialFlowState = {
    sources: [] as CreationToolSourceFile[],
    outputType: CreationToolOutputType.Gif,
    steps: [] as CreationToolStep[],
    stepFrames: {} as Partial<Record<CreationToolStep, CreationToolFrame[]>>,
    currentAudioId: '',
    isDraggingFile: false,
    pendingFiles: null as { sources: CreationToolSourceFile[]; outputType: CreationToolOutputType } | null,
}

export const createCreationStore = (init: CreationStoreInit): StoreApi<CreationState> =>
    createStore<CreationState>()((set, get) => ({
        ...initialFlowState,
        tool: init.tool,
        userRestrictionAccess: init.userRestrictionAccess,
        isLoggedIn: init.isLoggedIn,
        // Session-scoped like isLoggedIn/userRestrictionAccess above, not
        // flow state: deliberately left OUT of initialFlowState so reset()'s
        // `set({...initialFlowState})` doesn't clear it back to false in the
        // very call that's supposed to set it (see the field's own comment
        // in CreationState).
        isNavigatingAway: false,
        markNavigatingAway: () => set({ isNavigatingAway: true }),

        setFiles: (sources, outputType) => {
            // Legacy gates here, after the source is chosen — not on page entry.
            // Read isLoggedIn from the store (not init) so a sign-in that
            // happens after the first blocked attempt is honored on retry.
            if (CreationTool.requiresLogin(init.tool) && !get().isLoggedIn) {
                // Divergence from legacy (spec §13): legacy discards the chosen
                // sources here (manager.tsx:85-89), so after signing in the user
                // has to pick their file all over again. Stash them instead and
                // let resumePendingFiles replay this call on success.
                //
                // Dispose any earlier stash first, unless it holds the very
                // sources coming in — otherwise re-picking while the modal is
                // open leaks the previous selection's canvases.
                const { pendingFiles: previous } = get()
                if (previous) {
                    disposeSources(previous.sources.filter((source) => !sources.includes(source)))
                }
                set({ pendingFiles: { sources, outputType } })
                init.onRequireLogin()
                return
            }

            // A successful call consumes the stash; anything still held here was
            // superseded and its canvases are released above or below.
            if (get().pendingFiles) set({ pendingFiles: null })

            // Release the outgoing sources'/stepFrames' canvases before they're
            // dropped from state — browsers don't reclaim canvas memory just
            // because the reference is gone (see dispose.ts). Skip any
            // outgoing source that's reference-identical to one of the
            // incoming sources (e.g. onChangeOutputType revalidating the same
            // file) so it isn't disposed out from under itself.
            const { sources: outgoingSources, stepFrames: outgoingStepFrames } = get()
            disposeSources(outgoingSources.filter((source) => !sources.includes(source)))
            Object.values(outgoingStepFrames).forEach((frames) => disposeFrames(frames))

            const steps = getCreationSteps(sources, init.tool, outputType)
            // Re-arms RequireSources: isNavigatingAway is a one-way latch
            // (only markNavigatingAway ever sets it true) — if the post-
            // upload hard navigation to the Django-served GIF page never
            // completes (cancelled, failed), this same layout mount would
            // otherwise stay permanently disarmed, letting a later empty-
            // sources state (e.g. this very call, if steps ends up empty)
            // render a blank step instead of redirecting. Starting a new
            // creation is the natural point to clear it — a fresh sources
            // set means any prior "leaving on purpose" is no longer true.
            set({ sources, outputType, steps, stepFrames: {}, isNavigatingAway: false })

            if (steps.length) {
                init.navigate(`${init.basePath}${CreationToolStep.route(steps[0])}`)
            }
        },

        resumePendingFiles: () => {
            const { pendingFiles } = get()
            if (!pendingFiles) return
            // Clear before replaying: setFiles re-enters this store, and the
            // gate above must not see a stale stash if it blocks again.
            set({ pendingFiles: null })
            get().setFiles(pendingFiles.sources, pendingFiles.outputType)
        },

        setSources: (sources) => set({ sources }),

        replaceSources: (sources) => {
            // setSources is a bare setter and disposes nothing. The trim step swaps
            // sources[0] for a trimmed File and slices its frame list, so without this
            // every pre-trim canvas leaks — browsers don't reclaim canvas memory just
            // because the reference is gone (see dispose.ts). Compared at the canvas
            // level, not whole-source-object identity: the trim step replaces a
            // source with a new object that still carries some of the same canvases
            // forward (its `preview`, and the frames the trim keeps), and a
            // reference-identity check would dispose those out from under the
            // replacement. disposeUnreachableCanvases only releases canvases that
            // aren't reachable from any incoming source, regardless of which source
            // object — old or new — happens to hold them.
            const { sources: outgoing } = get()
            disposeUnreachableCanvases(outgoing, sources)
            set({ sources })
        },
        setOutputType: (outputType) => set({ outputType }),
        setCurrentAudioId: (currentAudioId) => set({ currentAudioId }),
        setIsDraggingFile: (isDraggingFile) => set({ isDraggingFile }),
        setIsLoggedIn: (isLoggedIn) => set({ isLoggedIn }),
        setUserRestrictionAccess: (userRestrictionAccess) => set({ userRestrictionAccess }),
        saveFrames: (frames, step) => set((state) => ({ stepFrames: { ...state.stepFrames, [step]: frames } })),
        requireLogin: (onSuccess) => init.onRequireLogin(onSuccess),

        reset: () => {
            const { sources, stepFrames, pendingFiles } = get()
            disposeSources(sources)
            // A stash left behind by a login prompt the user never completed
            // holds canvases too, and initialFlowState clears the reference.
            if (pendingFiles) disposeSources(pendingFiles.sources.filter((source) => !sources.includes(source)))
            Object.values(stepFrames).forEach((frames) => disposeFrames(frames))
            set({ ...initialFlowState })
        },
    }))
